import type { MatchRelevance } from '$shared/constants/MatchRelevance';
import { UserActionResolution } from '$shared/constants/UserActionResolution';
import type { IDatasetMatch } from '$shared/interfaces/api/IDatasetMatch';
import BaseModel from '../BaseModel';
import DateString from '../DateString';
import { DatasetMatchData } from './DatasetMatchData';
import { DatasetScan } from './DatasetScan';
import type DatasetScanParam from '../DatasetScanParam';
import { VerificationData } from '$shared/lib/appData/VerificationData';

export class DatasetMatch extends BaseModel<IDatasetMatch> {
  declare public data: DatasetMatchData;
  declare public scan: DatasetScan;

  public get id(): string {
    return this.response?.id || '';
  }

  public get datasetTimestamp(): DateString {
    return DateString.create(this.response.datasetTimestamp)!;
  }

  public get recordTimestamp(): DateString {
    return DateString.create(this.response.recordTimestamp)!;
  }

  public get scanId(): string {
    return this.response?.scanId || '';
  }

  public get userActionNeeded(): boolean {
    return !!this.response?.userActionNeeded;
  }

  public get userActionResolution(): UserActionResolution | null {
    return this.response.userActionResolution || null;
  }

  public get userActionResolutionNote(): string {
    return this.response?.userActionResolutionNote || '';
  }

  public get matchRelevance(): MatchRelevance | null {
    return this.response?.matchRelevance || null;
  }

  public get isIgnored(): boolean {
    return !!this.response?.isIgnored;
  }

  public get isReview(): boolean {
    return this.userActionNeeded;
  }

  public get isAccepted(): boolean {
    return !this.isReview && this.userActionResolution === UserActionResolution.Accept;
  }

  public get isRejected(): boolean {
    return this.isIgnored && this.userActionResolution === UserActionResolution.Reject;
  }

  public get typeName(): string {
    return VerificationData.getDatasetName(this.scan?.type) || '';
  }

  public toList(): IDataList {
    // Load required meta infos
    const __meta: ObjectType<any> = {
      id: this.id,
      name: this.data?.record?.getName(),
      verifiedOn: this.scan?.verifiedOn || '',
      isReview: this.isReview,
      matchRelevance: this.matchRelevance,
      isAccepted: this.isAccepted
    };

    const result = this.data?.record?.toList();
    this.data?.record?.highlightRecordPropWithParameters(result);

    return { ...result, __meta };
  }

  protected transformResponse(): void {
    this.scan = new DatasetScan(this.response.scan!);
    this.data = new DatasetMatchData(this.scan.type, this.response.data);
  }

  static getPositiveParams(matches: DatasetMatch[]): DatasetScanParam[] {
    let positiveParams: DatasetScanParam[] = [];
    matches?.forEach((match) => {
      positiveParams = positiveParams.concat(match.data?.positiveParameters);
    });
    return positiveParams;
  }

  static getNegativeParams(matches: DatasetMatch[]): DatasetScanParam[] {
    let negativeParams: DatasetScanParam[] = [];
    matches?.forEach((match) => {
      negativeParams = negativeParams.concat(match.data?.negativeParameters);
    });
    return negativeParams;
  }

  public static hasReviewMatch(matches: DatasetMatch[]): boolean {
    return !!matches?.some((match) => match.isReview);
  }
}
